/**
 * The single status / label pill for every app (BL-1712, ratifying the `Badge`
 * row of the ADR 030 — Design Canon).
 *
 * The merge collapses two implementations of the same job: admin's canonical
 * pill (BL-916 / BL-981 / BL-1580 — tones, a leading dot, a live pulse, an icon
 * slot, a count shape, a truncating label with a title fallback) and bidder's
 * label chip (a span with three colour variants). The core keeps admin's
 * STRUCTURE, the richer of the two, and every APPEARANCE value arrives through
 * the `dialect`.
 *
 * WHY sizing is dialect, not core: admin's 24px bordered pill (BL-1580) and
 * bidder's compact 10px chip are both deliberate, and a core-owned height would
 * restyle one of them. ADR 030 merges implementations, never looks — unifying
 * the two dialects is a regression, not a cleanup.
 *
 * WHY the pulse is an element the dialect supplies: the live dot's colour is a
 * per-app ruling (ADR 030 — Design Canon, the 2026-09-07 amendment), and its
 * animation is the app's own component (admin's `ActiveDot`). The core only
 * decides WHEN it renders: `dot` + `pulse`, never with an `icon`.
 *
 * `badgeShellClassName` exists for a control that must BE the pill but cannot
 * be a `<span>` — the status dropdowns' `<button>` triggers. Composing it keeps
 * one source for the shell; a wrapper component would regrow into a fork.
 */
'use client';

import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';

import type { BadgeTone } from '../types/badge-tone';

/**
 * What the shell accepts: a semantic tone, or `custom` (BL-1580) — the shell
 * with NO tone classes, for a pill whose colour is data (a lot-settings group's
 * hue) supplied via `style` / `className`. Kept out of `BadgeTone` so every
 * `Record<BadgeTone, …>` stays a semantic map.
 */
export type BadgeShellTone = BadgeTone | 'custom';

/** Per-app appearance binding — class names and the live dot, no structure. */
export interface BadgeDialect {
    /** The pill itself — shape, size, typography. */
    base: string;
    /** Added to `base` for a numeric count badge (`count`). */
    count?: string;
    /** Tone → colour classes. A tone the dialect leaves out renders uncoloured. */
    tones: Partial<Record<BadgeShellTone, string>>;
    /** Tone → leading-dot colour, for `dot`. */
    dots?: Partial<Record<BadgeTone, string>>;
    /** The element drawn for `dot` + `pulse` — the app's own live indicator. */
    pulseDot?: ReactNode;
}

/** Props for {@link Badge}. */
export interface BadgeProps {
    /** Per-app appearance binding. See {@link BadgeDialect}. */
    dialect: BadgeDialect;
    /** Semantic tone (or `custom` with a `style` / `className` colour). Defaults to `neutral`. */
    tone?: BadgeShellTone;
    /** Badge label. */
    children: ReactNode;
    /**
     * Leading status dot. Use ONLY for in-flight states (Active / Pending /
     * Processing); terminal states get none. Ignored when `icon` is set.
     */
    dot?: boolean;
    /**
     * Upgrades the leading dot to the dialect's live `pulseDot`. Reserved for
     * states that are live RIGHT NOW. Requires `dot`; ignored when `icon` is set.
     */
    pulse?: boolean;
    /** Optional leading icon. Takes precedence over `dot`. */
    icon?: ReactNode;
    /** Numeric count badge — applies the dialect's `count` classes; the digits are not truncated. */
    count?: boolean;
    /** Extra classes (margin, etc.). */
    className?: string;
    /** Inline colour for `tone="custom"`. Pass a tone OR a style, not both. */
    style?: CSSProperties;
    /** Native `title` tooltip. */
    title?: string;
    /** Test hook forwarded to the rendered pill. */
    'data-testid'?: string;
    /**
     * Suppress the auto-derived native `title` (the string label) — for a badge
     * inside its own hover popover (BL-1061), or a status badge that should read
     * no title at all (BL-1380). An explicit `title` still wins.
     */
    disableTitleFallback?: boolean;
}

/**
 * The pill's shell as a class string: the dialect base, its count classes when
 * `count`, and the tone's colours. Exactly what {@link Badge} renders with.
 *
 * @param dialect - the app's {@link BadgeDialect}
 * @param opts.tone - semantic tone (default `neutral`).
 * @param opts.count - numeric count badge.
 * @returns The base + count + tone classes.
 */
export function badgeShellClassName(
    dialect: BadgeDialect,
    { tone = 'neutral', count = false }: { tone?: BadgeShellTone; count?: boolean } = {},
): string {
    return clsx(dialect.base, count && dialect.count, dialect.tones[tone]);
}

/**
 * The shared badge. Structure only; the dialect paints it.
 *
 * @param props - {@link BadgeProps}
 * @returns An inline pill.
 */
export function Badge({
    dialect,
    tone = 'neutral',
    children,
    dot = false,
    pulse = false,
    icon,
    count = false,
    className,
    style,
    title,
    disableTitleFallback = false,
    'data-testid': dataTestId,
}: BadgeProps) {
    // BL-1061: in a width-constrained container the label ellipsizes; the full
    // string stays reachable as the native title unless the caller opts out.
    const resolvedTitle =
        title ?? (disableTitleFallback ? undefined : typeof children === 'string' ? children : undefined);
    return (
        <span
            title={resolvedTitle}
            data-testid={dataTestId}
            // BL-2192: the tone as an automation handle, so a check can read
            // which shared tone a label wears without matching classes.
            data-tone={tone}
            style={style}
            className={clsx(badgeShellClassName(dialect, { tone, count }), className)}
        >
            {icon ? (
                <span className="inline-flex shrink-0 items-center">{icon}</span>
            ) : dot && pulse ? (
                dialect.pulseDot
            ) : dot ? (
                // A custom-coloured pill has no tone dot; `bg-current` takes its text colour.
                <span
                    className={clsx(
                        'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
                        tone === 'custom' ? 'bg-current' : dialect.dots?.[tone],
                    )}
                    aria-hidden
                />
            ) : null}
            {count ? children : <span className="min-w-0 truncate">{children}</span>}
        </span>
    );
}
